import { CloseIcon } from '@repo/icons';
import { Button } from '@repo/ui/button';
import { Checkbox } from '@repo/ui/checkbox';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/dialog';
import { useTranslations } from 'next-intl';
import React from 'react';
import { useRouter } from '@/i18n/routing';
import { useAppStore } from '@/providers/AppStoreProvider';

interface CloseGameDialogProps {
  open: boolean;
  handleOpen: (state: boolean) => void;
}

/**
 * The CloseGameDialog component is a dialog that prompts the user to close the current game and go back to the lobby.
 */
const CloseGameDialog = ({ open, handleOpen }: CloseGameDialogProps) => {
  const t = useTranslations('close-game-dialog');

  const router = useRouter();

  const { hideCloseGameModal, profileUuid, closeGameModal, prevPathname } = useAppStore((state) => ({
    closeGameModal: state.closeGameModal,
    hideCloseGameModal: state.hideCloseGameModal,
    prevPathname: state.prevPathname,
    profileUuid: state.profileUuid,
  }));

  const handleBackNavigation = () => {
    handleOpen(false);
    if (prevPathname?.includes('casino')) {
      router.push(prevPathname as any);
    } else {
      router.push('/casino');
    }
  };

  const handleKeepPlaying = () => {
    handleOpen(false);
  };

  const handleHideCloseGameModalClick = () => {
    hideCloseGameModal();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpen}
    >
      <DialogContent className="flex w-11/12 flex-col items-start justify-between gap-y-8 overflow-x-hidden p-4 sm:w-[380px]">
        <DialogHeader className="sticky left-0 top-0 flex h-[20px] w-full flex-row items-center justify-between vt-font-highlight-font-family vt-text-size-big vt-headings-text sm:vt-text-size-big-sm">
          <DialogTitle className="vt-font-highlight-font-family vt-body-text-darker">{t('title')}</DialogTitle>
          <DialogClose asChild>
            <Button
              size="sm"
              variant="blank"
            >
              <CloseIcon />
            </Button>
          </DialogClose>
        </DialogHeader>
        <div className="flex w-full flex-col items-center justify-center space-y-4">
          <div className="text-center vt-text-size-medium sm:vt-text-size-medium-sm">{t('subtitle')}</div>
          <div className="flex items-center justify-center space-x-2 text-center vt-text-size-small sm:vt-text-size-small-sm">
            <Checkbox
              onCheckedChange={handleHideCloseGameModalClick}
              checked={closeGameModal[profileUuid as string] || false}
            />
            <span>{t('do-not-ask-again-checkbox')}</span>
          </div>
        </div>
        <div className="max-sm:justify-between flex w-full flex-nowrap gap-x-2">
          <Button
            onClick={handleBackNavigation}
            variant="secondary"
            className="w-full vt-font-highlight-font-family vt-text-size-small sm:vt-text-size-small-sm"
          >
            {t('close-button')}
          </Button>
          <Button
            onClick={handleKeepPlaying}
            className="w-full vt-font-highlight-font-family vt-text-size-small sm:vt-text-size-small-sm"
          >
            {t('cancel-button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CloseGameDialog;
